'use client';

import type { Point } from './types';

/**
 * @description generate a new array of points representing the convex hull of the given set of points
 * @see https://www.nayuki.io/page/convex-hull-algorithm, https://www.nayuki.io/res/convex-hull-algorithm/convex-hull.ts
 */
export function getHull<P extends Point>(points: Readonly<Array<P>>): Array<P> {
  const newPoints: Array<P> = [...points].sort((a: Point, b: Point) => {
    if (a.x < b.x) return -1;
    if (a.x > b.x) return +1;
    if (a.y < b.y) return -1;
    if (a.y > b.y) return +1;
    return 0;
  });
  return getHullPresorted(newPoints);
}

/**
 * @description Returns the convex hull, assuming that each points[i] <= points[i + 1]. Runs in O(n) time.
 */
export function getHullPresorted<P extends Point>(
  points: Readonly<Array<P>>
): Array<P> {
  // handle edge cases
  if (points.length <= 1) return [...points];
  // get the upper and lower hulls
  const upperHull = buildHull(points, false);
  const lowerHull = buildHull(points, true);
  // check if both hulls are the same and return the upper hull if they are
  if (areBothHullsEqual(upperHull, lowerHull)) return upperHull;
  // or the combined hulls if they are not
  return [...upperHull, ...lowerHull];
}

/**
 * @description helper function used to determine if both hulls are equal
 */
function areBothHullsEqual<P extends Point>(
  upperHull: Readonly<Array<P>>,
  lowerHull: Readonly<Array<P>>
): boolean {
  return (
    upperHull.length === 1 &&
    lowerHull.length === 1 &&
    upperHull[0].x === lowerHull[0].x &&
    upperHull[0].y === lowerHull[0].y
  );
}

/**
 * @description helper function used to construct the convex hull of a set of points
 */
function buildHull<P extends Point>(
  points: Readonly<Array<P>>,
  isReversed = false
): Array<P> {
  // maintain the convex hull of points
  const hull: Array<P> = [];
  const len = points.length;
  // iterate through the points and construct the hull
  for (let i = 0; i < len; i++) {
    const p = points[isReversed ? len - 1 - i : i];
    // if the hull has more than one point and the last two points and the current point make a right turn
    while (hull.length >= 2) {
      const [r, q] = hull.slice(-2);
      if ((q.x - r.x) * (p.y - r.y) >= (q.y - r.y) * (p.x - r.x)) hull.pop();
      else break;
    }
    hull.push(p);
  }
  hull.pop();
  return hull;
}
